import React, { useState, useEffect } from 'react';
import { api, loadBuildingsForSelector } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import {
  serviceFeeManagementApi,
  FeeStatisticsRow,
} from '../../api/serviceFeeManagementApi';
import { downloadExcel, sanitizeExportFilename } from '../../utils/excelExport';
import toast from 'react-hot-toast';

interface Building {
  id: string;
  name: string;
}

interface FeeStatisticsTabProps {
  billingPeriod: string;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(amount)) + 'đ';

const flattenStatisticsRows = (rows: FeeStatisticsRow[]): FeeStatisticsRow[] => {
  const result: FeeStatisticsRow[] = [];
  for (const row of rows) {
    result.push(row);
    if (row.children?.length) {
      result.push(...flattenStatisticsRows(row.children));
    }
  }
  return result;
};

export const FeeStatisticsTab: React.FC<FeeStatisticsTabProps> = ({ billingPeriod }) => {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState('');
  const [rows, setRows] = useState<FeeStatisticsRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    }).catch(console.error);
  }, [user]);

  const loadStatistics = async () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    setLoading(true);
    try {
      const data = await serviceFeeManagementApi.getStatistics(buildingId, billingPeriod);
      setRows(data);
    } catch {
      toast.error('Không thể tải thống kê phí');
    } finally {
      setLoading(false);
    }
  };

  const flatRows = flattenStatisticsRows(rows);

  const handleExport = () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    if (flatRows.length === 0) {
      toast.error('Không có dữ liệu để xuất. Vui lòng tải thống kê trước.');
      return;
    }

    const buildingName = buildings.find((b) => b.id === buildingId)?.name || buildingId;
    const headers = [
      'STT',
      'NỘI DUNG',
      'PHẢI THU TRƯỚC THUẾ',
      'THUẾ VÀ PHỤ PHÍ',
      'MIỄN GIẢM',
      'TỔNG PHẢI THU',
      'ĐÃ THU',
      'CÔNG NỢ',
    ];
    const dataRows = flatRows.map((row, idx) => [
      idx === 0 ? 'I' : idx,
      row.label,
      row.receivableBeforeTax,
      row.taxAndSurcharge,
      row.exemption,
      row.totalReceivable,
      row.collected,
      row.debt,
    ]);

    const filename = `thong-ke-phi-${sanitizeExportFilename(buildingName)}-${billingPeriod.replace('/', '-')}.xlsx`;
    downloadExcel('Thống kê phí', headers, dataRows, filename);
    toast.success('Đã xuất file Excel');
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {shouldShowBuildingSelector(buildings) && (
        <select
          value={buildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
          className="px-3 py-2 border border-gray-300 rounded-lg min-w-[200px]"
        >
          <option value="">Chọn chung cư</option>
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        )}
        <button
          onClick={loadStatistics}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {loading ? 'Đang tải...' : 'Làm mới dữ liệu'}
        </button>
        <button
          onClick={handleExport}
          disabled={loading || flatRows.length === 0}
          className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          Xuất dữ liệu
        </button>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">STT</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">NỘI DUNG</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">PHẢI THU TRƯỚC THUẾ</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">THUẾ VÀ PHỤ PHÍ</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">MIỄN GIẢM</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">TỔNG PHẢI THU</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">ĐÃ THU</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">CÔNG NỢ</th>
            </tr>
          </thead>
          <tbody>
            {flatRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  Chọn chung cư và kỳ tính phí, sau đó nhấn &quot;Làm mới dữ liệu&quot;
                </td>
              </tr>
            ) : (
              flatRows.map((row, idx) => (
                <tr
                  key={row.id}
                  className={`border-t border-gray-100 ${row.level === 0 ? 'bg-gray-50 font-bold' : row.level === 1 ? 'font-semibold' : ''}`}
                >
                  <td className="px-4 py-2 text-gray-600">{idx === 0 ? 'I' : idx}</td>
                  <td className="px-4 py-2" style={{ paddingLeft: `${16 + row.level * 20}px` }}>
                    {row.label}
                  </td>
                  <td className="px-4 py-2 text-right">{formatCurrency(row.receivableBeforeTax)}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(row.taxAndSurcharge)}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(row.exemption)}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(row.totalReceivable)}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(row.collected)}</td>
                  <td className="px-4 py-2 text-right text-red-600 font-medium">
                    {formatCurrency(row.debt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeeStatisticsTab;
